import { useCallback, useEffect, useRef, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { usePermissions } from "contexts/PermissionsContext"

import { PERMISSIONS } from "constants/permissions"

import { AdminTokenService } from "services/token/AdminTokenService"
import { BulkGrantFilters, BulkGrantJob, RecipientPolicy } from "services/token/type"

import { isDecimalString, tokenErrorCopy, Translate, useIdempotencyKey } from "../helpers/token.helpers"
import { useTokenIdentity } from "../helpers/useTokenIdentity"

import { TokenField } from "./shared/TokenField"

/**
 * Bulk grant campaigns — a fixed amount to a filtered audience.
 *
 * 🔴 This mass-mints. It is the single most dangerous control in the panel, and
 * it is gated three ways: the tenant must run the token module, the operator
 * needs `page.token.bulk_grant` to see this tab, and `token.bulk_grant.execute`
 * to start anything. None of those is a UI convenience — the server re-checks
 * every one.
 *
 * The confirmation phrase carries the audience size deliberately. A fixed phrase
 * becomes muscle memory and stops nothing; one that has to be read off the
 * preview cannot be typed without noticing how many players it covers.
 *
 * Two independent choices drive a campaign and conflating them is the easiest
 * way to send money to the wrong people: the FILTERS decide which players are
 * considered, the RECIPIENT POLICY decides which of those actually get credited.
 */

const POLL_MS = 3_000

/**
 * The recipient policy, in the operator's words.
 *
 * ZERO_BALANCE is first and is the default because it is the conservative one:
 * it cannot give anybody a second helping. ALL is last and carries the warning,
 * because it is the only option that tops up players who already hold tokens.
 */
const RECIPIENT_OPTIONS: Array<{ value: RecipientPolicy; labelKey: string; blurbKey: string }> = [
  { value: "ZERO_BALANCE", labelKey: "token.policyZeroBalance", blurbKey: "token.policyZeroBalanceBlurb" },
  { value: "NEVER_PURCHASED", labelKey: "token.policyNeverPurchased", blurbKey: "token.policyNeverPurchasedBlurb" },
  { value: "ALL", labelKey: "token.policyAll", blurbKey: "token.policyAllBlurb" }
]

/** Job status → translation key, for the lowercase word in the progress header. */
const STATUS_KEYS: Record<string, string> = {
  PENDING: "token.statusPending",
  RUNNING: "token.statusRunning",
  COMPLETED: "token.statusCompleted",
  FAILED: "token.statusFailed",
  CANCELLED: "token.statusCancelled"
}

/** Below this, elapsed/processed is noise and an ETA built on it teaches distrust. */
const ETA_MIN_PROCESSED = 500

const isTerminal = (job: BulkGrantJob): boolean =>
  job.status === "COMPLETED" || job.status === "FAILED" || job.status === "CANCELLED"

function formatEta(job: BulkGrantJob, t: Translate): string | null {
  if (job.processed < ETA_MIN_PROCESSED || !job.started_at) return null
  const remaining = job.total_users - job.processed
  if (remaining <= 0) return null

  const elapsedMs = Date.now() - new Date(job.started_at).getTime()
  const etaMs = (elapsedMs / job.processed) * remaining
  const minutes = Math.ceil(etaMs / 60_000)
  return minutes <= 1 ? t("token.etaOneMinute") : t("token.etaMinutes", { minutes })
}

export const BulkGrantTab = ({ tenant }: { tenant: string }) => {
  const { t } = useTranslation("translation", { useSuspense: false }) as { t: Translate }
  const { ticker } = useTokenIdentity(tenant)
  const { has } = usePermissions()
  const canExecute = has(PERMISSIONS.TOKEN_BULK_GRANT_EXECUTE)

  const [amount, setAmount] = useState("")
  const [reason, setReason] = useState("")
  const [filters, setFilters] = useState<BulkGrantFilters>({})
  const [recipientPolicy, setRecipientPolicy] = useState<RecipientPolicy>("ZERO_BALANCE")
  const [preview, setPreview] = useState<number | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [confirmPhrase, setConfirmPhrase] = useState("")
  const [job, setJob] = useState<BulkGrantJob | null>(null)
  const [cooldown, setCooldown] = useState(0)
  const [history, setHistory] = useState<BulkGrantJob[]>([])
  const [submitting, setSubmitting] = useState(false)

  const { key: idempotencyKey, reset: resetIdempotencyKey } = useIdempotencyKey()
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const refresh = useCallback(async () => {
    const active = await AdminTokenService.getActiveBulkGrant(tenant)
    if (active.ok) {
      setJob(active.data?.job ?? null)
      setCooldown(active.data?.cooldown_seconds_remaining ?? 0)
    }
    const list = await AdminTokenService.listBulkGrants(tenant)
    if (list.ok) setHistory(list.data?.jobs ?? [])
  }, [tenant])

  useEffect(() => {
    void refresh()
  }, [refresh])

  // Poll only while something is actually moving. A finished campaign is static,
  // and a tab left open on one should not keep a request loop alive.
  useEffect(() => {
    const running = job && !isTerminal(job)
    if (!running) {
      if (pollRef.current) clearInterval(pollRef.current)
      pollRef.current = null
      return
    }
    pollRef.current = setInterval(() => void refresh(), POLL_MS)
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [job, refresh])

  const runPreview = async () => {
    setPreviewing(true)
    const res = await AdminTokenService.previewBulkGrant(tenant, filters)
    setPreviewing(false)
    if (!res.ok) {
      toast.error(tokenErrorCopy(t, res.code, res.message))
      return
    }
    setPreview(res.data?.total_users ?? 0)
    // Any change to the audience invalidates a phrase typed against the old one.
    setConfirmPhrase("")
  }

  const expectedPhrase = preview === null ? "" : `GRANT ${amount || "?"} TO ${preview} USERS`
  const totalMint = preview !== null && isDecimalString(amount) ? Number(amount) * preview : null

  const canSubmit =
    canExecute &&
    !submitting &&
    preview !== null &&
    preview > 0 &&
    isDecimalString(amount) &&
    Number(amount) > 0 &&
    reason.trim().length >= 10 &&
    confirmPhrase === expectedPhrase &&
    cooldown === 0 &&
    !job

  const submit = async () => {
    if (!canSubmit || preview === null) return
    setSubmitting(true)
    const res = await AdminTokenService.createBulkGrant(
      tenant,
      reason.trim(),
      amount,
      filters,
      recipientPolicy,
      preview,
      idempotencyKey
    )
    setSubmitting(false)

    if (!res.ok) {
      toast.error(tokenErrorCopy(t, res.code, res.message))
      void refresh()
      return
    }
    toast.success(t("token.campaignStartedToast"))
    setJob(res.data?.job ?? null)
    setConfirmPhrase("")
    // A new key for the next campaign; reusing this one would make a second
    // legitimate campaign replay the first one's response.
    resetIdempotencyKey()
    void refresh()
  }

  const cancel = async () => {
    if (!job) return
    const why = window.prompt(t("token.cancelPrompt"))?.trim()
    if (!why) return
    const res = await AdminTokenService.cancelBulkGrant(tenant, job.id, why)
    if (!res.ok) {
      toast.error(tokenErrorCopy(t, res.code, res.message))
      return
    }
    toast.success(t("token.campaignCancelledToast"))
    void refresh()
  }

  const running = job && !isTerminal(job)

  return (
    <div className="flex min-w-0 flex-col gap-6">
      <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-sm">
        <p className="font-semibold text-amber-300">{t("token.bulkWarningTitle", { ticker })}</p>
        <p className="mt-1 text-amber-200/80">{t("token.bulkWarningBody")}</p>
      </div>

      {running ? (
        <ProgressPanel job={job} ticker={ticker} t={t} onCancel={canExecute ? cancel : undefined} />
      ) : (
        <div className="flex min-w-0 flex-col gap-4 rounded-lg border border-white/10 p-4">
          <h3 className="text-lg font-semibold">{t("token.newCampaign")}</h3>

          {!canExecute && <p className="text-sm text-gray-400">{t("token.bulkNoExecute")}</p>}

          {cooldown > 0 && <p className="text-sm text-amber-300">{t("token.bulkCooldown", { minutes: Math.ceil(cooldown / 60) })}</p>}

          <TokenField
            label={t("token.amountPerPlayer", { ticker })}
            value={amount}
            onChange={(v) => {
              setAmount(v)
              setConfirmPhrase("")
            }}
            placeholder="10"
          />

          <fieldset className="flex flex-col gap-2 rounded border border-white/10 p-3">
            <legend className="px-1 text-sm font-medium">{t("token.whoGetsCredited")}</legend>
            {RECIPIENT_OPTIONS.map((opt) => (
              <label key={opt.value} className="flex items-start gap-2 text-sm">
                <input
                  type="radio"
                  name="recipient-policy"
                  className="radio radio-sm mt-0.5 shrink-0"
                  checked={recipientPolicy === opt.value}
                  onChange={() => {
                    setRecipientPolicy(opt.value)
                    // The count does not change, but the phrase was typed
                    // against a different campaign — make it be re-confirmed.
                    setConfirmPhrase("")
                  }}
                />
                <span>
                  <span className={opt.value === "ALL" ? "font-medium text-amber-300" : "font-medium"}>{t(opt.labelKey)}</span>
                  <span className="block text-xs text-gray-400">{t(opt.blurbKey)}</span>
                </span>
              </label>
            ))}
            {recipientPolicy === "ALL" && (
              <p className="text-xs text-amber-300">{t("token.policyAllWarning", { ticker })}</p>
            )}
          </fieldset>

          <AudienceFilters
            filters={filters}
            onChange={(f) => {
              setFilters(f)
              setPreview(null)
              setConfirmPhrase("")
            }}
          />

          <div className="flex flex-wrap items-center gap-3">
            <button type="button" className="btn btn-sm w-full sm:w-auto" onClick={runPreview} disabled={previewing}>
              {previewing ? t("token.counting") : t("token.countAudience")}
            </button>
            {preview !== null && (
              <span className="text-sm">
                <strong>{t("token.audienceMatch", { players: preview.toLocaleString() })}</strong>
                {totalMint !== null && <> · {t("token.totalMint", { amount: totalMint.toLocaleString(), ticker })}</>}
              </span>
            )}
          </div>

          <TokenField
            label={t("token.reasonAuditLabel")}
            value={reason}
            onChange={setReason}
            placeholder={t("token.reasonAuditPlaceholder")}
          />

          {preview !== null && preview > 0 && (
            <div className="flex flex-col gap-2">
              <p className="text-sm text-gray-300">{t("token.confirmInstruction")}</p>
              <code className="break-all rounded bg-black/40 px-1 text-sm">{expectedPhrase}</code>
              <TokenField label={t("token.confirmationLabel")} value={confirmPhrase} onChange={setConfirmPhrase} placeholder={expectedPhrase} />
            </div>
          )}

          <button type="button" className="btn btn-error w-full sm:w-fit" onClick={submit} disabled={!canSubmit}>
            {submitting ? t("token.starting") : t("token.startCampaign")}
          </button>
        </div>
      )}

      <HistoryTable jobs={history} ticker={ticker} t={t} />
    </div>
  )
}

const ProgressPanel = ({ job, ticker, t, onCancel }: { job: BulkGrantJob; ticker: string; t: Translate; onCancel?: () => void }) => {
  const pct = job.total_users > 0 ? Math.round((job.processed / job.total_users) * 100) : 0
  const eta = formatEta(job, t)

  return (
    <div className="flex min-w-0 flex-col gap-3 rounded-lg border border-white/10 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-lg font-semibold">
          {t("token.grantingHeader", {
            amount: job.amount,
            ticker,
            status: STATUS_KEYS[job.status] ? t(STATUS_KEYS[job.status]) : job.status.toLowerCase()
          })}
        </h3>
        {onCancel && (
          <button type="button" className="btn btn-sm btn-outline" onClick={onCancel}>
            {t("token.cancel")}
          </button>
        )}
      </div>

      <div className="h-2 w-full overflow-hidden rounded bg-white/10">
        <div className="h-full bg-purple-500 transition-all" style={{ width: `${pct}%` }} />
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
        <span>{t("token.processedCount", { processed: job.processed.toLocaleString(), total: job.total_users.toLocaleString() })}</span>
        <span className="text-green-400">{t("token.grantedCount", { value: job.granted.toLocaleString() })}</span>
        <span className="text-gray-400">
          {t("token.skippedCount", { value: job.skipped.toLocaleString() })}
          {job.recipient_policy === "ZERO_BALANCE" && ` ${t("token.skippedZeroBalance")}`}
          {job.recipient_policy === "NEVER_PURCHASED" && ` ${t("token.skippedNeverPurchased")}`}
        </span>
        {eta && <span className="text-gray-400">{eta}</span>}
      </div>

      <p className="text-xs text-gray-500">{t("token.safeToClose")}</p>
    </div>
  )
}

const HistoryTable = ({ jobs, ticker, t }: { jobs: BulkGrantJob[]; ticker: string; t: Translate }) => {
  if (jobs.length === 0) return null

  return (
    <div className="min-w-0 rounded-lg border border-white/10 p-4">
      <h3 className="mb-3 text-lg font-semibold">{t("token.previousCampaigns")}</h3>
      {/*
        `min-w-0` on this card and on the tab root is what makes the scroller
        below work. A flex or grid item defaults to `min-width: auto`, which
        resolves to its content's intrinsic minimum — so the wide table pushes
        the card, the column and finally the page wider instead of scrolling
        inside this div. Removing either class brings back a horizontally
        scrolling page.
      */}
      <div className="w-full overflow-x-auto">
        <table className="table table-sm w-full min-w-[44rem]">
          <thead>
            <tr>
              <th>{t("token.colStarted")}</th>
              <th>{t("token.colAmount")}</th>
              <th>{t("token.colAudience")}</th>
              <th>{t("token.colRecipients")}</th>
              <th>{t("token.colGranted")}</th>
              <th>{t("token.colSkipped")}</th>
              <th>{t("token.colStatus")}</th>
              <th>{t("token.colReason")}</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => (
              <tr key={j.id}>
                <td>{new Date(j.created_at).toLocaleString()}</td>
                <td>
                  {j.amount} {ticker}
                </td>
                <td>{j.total_users.toLocaleString()}</td>
                <td>{(() => {
                  const opt = RECIPIENT_OPTIONS.find((o) => o.value === j.recipient_policy)
                  return opt ? t(opt.labelKey) : j.recipient_policy
                })()}</td>
                <td>{j.granted.toLocaleString()}</td>
                <td>{j.skipped.toLocaleString()}</td>
                <td>{STATUS_KEYS[j.status] ? t(STATUS_KEYS[j.status]) : j.status}</td>
                <td className="max-w-[10rem] truncate sm:max-w-xs" title={j.reason}>
                  {j.reason}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/**
 * The audience builder.
 *
 * Self-exclusion is absent on purpose — it is enforced server-side and is not
 * something an operator may switch off.
 */
const AudienceFilters = ({
  filters,
  onChange
}: {
  filters: BulkGrantFilters
  onChange: (f: BulkGrantFilters) => void
}) => {
  const { t } = useTranslation("translation", { useSuspense: false }) as { t: Translate }
  const set = (patch: Partial<BulkGrantFilters>) => onChange({ ...filters, ...patch })

  const numberField = (label: string, key: "loggedInLastDays" | "betLastDays" | "depositedLastDays" | "registeredLastDays") => (
    <label className="flex flex-wrap items-center gap-2 text-sm">
      <input
        type="number"
        min={1}
        className="input input-sm input-bordered w-24 shrink-0"
        value={filters[key] ?? ""}
        onChange={(e) => set({ [key]: e.target.value ? Number(e.target.value) : undefined } as Partial<BulkGrantFilters>)}
      />
      {label}
    </label>
  )

  const checkbox = (label: string, key: "kycVerified" | "emailVerified" | "activated") => (
    <label className="flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        className="checkbox checkbox-sm shrink-0"
        checked={filters[key] ?? false}
        onChange={(e) => set({ [key]: e.target.checked || undefined } as Partial<BulkGrantFilters>)}
      />
      {label}
    </label>
  )

  return (
    <fieldset className="flex flex-col gap-3 rounded border border-white/10 p-3">
      <legend className="px-1 text-sm font-medium">{t("token.audienceLegend")}</legend>

      {numberField(t("token.filterLoggedIn"), "loggedInLastDays")}
      {numberField(t("token.filterBet"), "betLastDays")}
      {numberField(t("token.filterDeposited"), "depositedLastDays")}
      {numberField(t("token.filterRegistered"), "registeredLastDays")}

      <label className="flex flex-wrap items-center gap-2 text-sm">
        <input
          type="number"
          min={0}
          className="input input-sm input-bordered w-28 shrink-0"
          value={filters.minTotalDepositCents ?? ""}
          onChange={(e) => set({ minTotalDepositCents: e.target.value ? Number(e.target.value) : undefined })}
        />
        {t("token.filterMinDeposit")}
      </label>

      <div className="flex flex-wrap gap-4">
        {checkbox(t("token.filterKyc"), "kycVerified")}
        {checkbox(t("token.filterEmail"), "emailVerified")}
        {checkbox(t("token.filterActivated"), "activated")}
      </div>

      <p className="text-xs text-gray-500">{t("token.selfExclusionNote")}</p>
    </fieldset>
  )
}
